import type { IrsLimitsSet } from '../../engine/types';
import type { HsaSavingsValues } from './config';

export interface HsaSavingsResult {
  /** Cumulative compounded balance at the end of each year, index 0 = year 1. */
  compoundedSavingsByYear: number[];
  /** Final year's compounded balance — may be negative if expenses exceed contributions. */
  totalSavings: number;
  taxSavings: number;
  /** True when contribution - medical expenses <= 0 — no savings will accumulate. */
  noSavings: boolean;
  contributionLimit: number;
  maxOutOfPocket: number;
}

/**
 * Ports hsa-savings-calc.js's calculateCompoundedSavingsArray() + the tax-savings
 * portion of resetGraph() verbatim, including the legacy roundToTwo rounding
 * quirk, so behavior matches the parity fixtures in test/parity/hsa-savings.json
 * exactly. See docs/calculator-engine-architecture.md §12 — this fixture is the
 * parity oracle; it was generated by running the legacy math directly, not
 * reimplemented from a description of it.
 */
function roundToTwo(num: number): number {
  return Number(Math.round(Number(`${num}e+2`)) + 'e-2');
}

function compoundedSavingsByYear(contributionAmt: number, medicalExpenses: number, years: number, interestRate: number): number[] {
  const savings = contributionAmt - medicalExpenses;
  const arr: number[] = [];
  let lastYearAmt = 0;

  for (let i = 0; i < years; i++) {
    let interest: number;
    let thisYearAmt: number;
    if (i === 0) {
      interest = savings * interestRate;
      thisYearAmt = savings + interest;
    } else {
      interest = (savings + lastYearAmt) * interestRate;
      thisYearAmt = savings + lastYearAmt + interest;
    }
    thisYearAmt = roundToTwo(thisYearAmt);
    arr.push(thisYearAmt);
    lastYearAmt = thisYearAmt;
  }

  return arr;
}

export function calculate(values: HsaSavingsValues, limits: IrsLimitsSet): HsaSavingsResult {
  const hsa = limits.current.hsa;
  const isFamily = values.planType === 'family';
  const contributionLimit = isFamily
    ? hsa.maxContributionFamily + hsa.catchupFamily
    : hsa.maxContributionSingle + hsa.catchupSingle;
  const maxOutOfPocket = isFamily ? hsa.maxOutOfPocketFamily : hsa.maxOutOfPocketSingle;

  const contributionAmt = values.contributionAmt ?? 0;
  const medicalExpenses = values.medicalExpenses ?? 0;
  const years = values.years ?? 0;
  const interestRate = (values.interestRatePct ?? 0) * 0.01;
  const fedTaxRate = (values.fedTaxPct ?? 0) * 0.01;
  const stateTaxRate = (values.stateTaxPct ?? 0) * 0.01;

  const byYear = compoundedSavingsByYear(contributionAmt, medicalExpenses, years, interestRate);
  const totalSavings = byYear.length > 0 ? byYear[byYear.length - 1] : 0;

  const totalSavingsNoInterest = contributionAmt * years;
  const taxSavings = Number(((totalSavingsNoInterest * fedTaxRate) + (totalSavingsNoInterest * stateTaxRate)).toFixed(2));

  return {
    compoundedSavingsByYear: byYear,
    totalSavings,
    taxSavings,
    noSavings: contributionAmt - medicalExpenses <= 0,
    contributionLimit,
    maxOutOfPocket,
  };
}
